import { License, UserLicense, PaymentIntent, PaymentProcessRequest, PaymentProcessResponse, PaymentTransaction, CreatePaymentIntentRequest, ProcessPaymentRequest, PaymentResponse } from '../types/payment.types';
import { store } from '../store';

const API_URL = process.env.REACT_APP_API_URL;

// Custom error class for better error handling
class LicenseError extends Error {
    constructor(
        message: string,
        public code?: string,
        public status?: number,
        public details?: any
    ) {
        super(message);
        this.name = 'LicenseError';
    }
}

// Helper function to handle API responses
async function handleResponse(response: Response, defaultError: string) {
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new LicenseError(
            errorData.message || defaultError,
            errorData.code,
            response.status,
            errorData.details
        );
    }
    return response.json();
}

export const LicenseService = {
    async getDefaultLicense(): Promise<License> {
        try {
            const response = await fetch(`${API_URL}/licenses/default`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include' // Send cookies for session auth
            });

            return handleResponse(response, 'Failed to fetch default license');
        } catch (error) {
            console.error('Error fetching default license:', error);
            if (error instanceof LicenseError) {
                throw error;
            }
            throw new LicenseError(
                'Failed to fetch default license',
                'FETCH_ERROR',
                500
            );
        }
    },

    async createPaymentIntent(data: CreatePaymentIntentRequest): Promise<PaymentIntent> {
        try {
            // const token = store.getState().auth.token;
            // if (!token) throw new Error('Authentication required');

            const response = await fetch(`${API_URL}/licenses/payments/create-intent`, {
                method: 'POST',
                headers: {
                    // 'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to create payment intent');
            }

            return response.json();
        } catch (error) {
            console.error('Error creating payment intent:', error);
            throw error;
        }
    },

    async processPayment(data: ProcessPaymentRequest): Promise<PaymentResponse> {
        try {
            // const token = store.getState().auth.token;
            // if (!token) throw new Error('Authentication required');

            const response = await fetch(`${API_URL}/licenses/payments/process`, {
                method: 'POST',
                headers: {
                    // 'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Payment processing failed');
            }

            return response.json();
        } catch (error) {
            console.error('Error processing payment:', error);
            throw error;
        }
    },

    async getLicensesByTeam(teamId: string): Promise<UserLicense[]> {
        try {
            const response = await fetch(`${API_URL}/licenses/team/${teamId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });
            return handleResponse(response, 'Failed to fetch team licenses');
        } catch (error) {
            console.error('Error fetching team licenses:', error);
            if (error instanceof LicenseError) {
                throw error;
            }
            throw new LicenseError(
                'Failed to fetch team licenses',
                'FETCH_ERROR',
                500
            );
        }
    },

    async getCurrentLicense(teamId: string): Promise<UserLicense | null> {
        try {
            const response = await fetch(`${API_URL}/licenses/team/${teamId}/current`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });
            return handleResponse(response, 'Failed to fetch current license');
        } catch (error) {
            console.error('Error fetching current license:', error);
            if (error instanceof LicenseError) {
                throw error;
            }
            throw new LicenseError(
                'Failed to fetch current license',
                'FETCH_ERROR',
                500
            );
        }
    },

    async getLicenseUsageHistory(licenseId: string): Promise<any> {
        try {
            const response = await fetch(`${API_URL}/licenses/${licenseId}/usage-history`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });
            return handleResponse(response, 'Failed to fetch license usage history');
        } catch (error) {
            console.error('Error fetching license usage history:', error);
            if (error instanceof LicenseError) {
                throw error;
            }
            throw new LicenseError(
                'Failed to fetch license usage history',
                'FETCH_ERROR',
                500
            );
        }
    },

    async checkLicenseAvailability(teamId: string): Promise<{
        canPurchase: boolean;
        message: string;
        currentLicense?: UserLicense;
    }> {
        try {
            const response = await fetch(`${API_URL}/licenses/team/${teamId}/availability`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });
            return handleResponse(response, 'Failed to check license availability');
        } catch (error) {
            console.error('Error checking license availability:', error);
            if (error instanceof LicenseError) {
                throw error;
            }
            throw new LicenseError(
                'Failed to check license availability',
                'FETCH_ERROR',
                500
            );
        }
    },

    async getTeamLicenses(teamId: string): Promise<License[]> {
        try {
            const response = await fetch(`${API_URL}/licenses/team/${teamId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to fetch team licenses');
            }
            return response.json();
        } catch (error) {
            console.error('Error fetching team licenses:', error);
            throw error;
        }
    },

    async getTeamTransactions(teamId: string): Promise<PaymentTransaction[]> {
        try {
            const response = await fetch(`${API_URL}/licenses/${teamId}/transactions`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to fetch team transactions');
            }
            const data = await response.json();
            return data.data || [];
        } catch (error) {
            console.error('Error fetching team transactions:', error);
            throw error;
        }
    },

    async getTeamLicenseCapacity(teamId: string): Promise<{
        total_limit: number;
        used_slots: number;
        available_slots: number;
    }> {
        try {
            const response = await fetch(`${API_URL}/licenses/team/${teamId}/capacity`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to fetch license capacity');
            }
            return response.json();
        } catch (error) {
            console.error('Error fetching license capacity:', error);
            throw error;
        }
    },

    async createTrialLicense(teamId: string): Promise<License> {
        try {
            const response = await fetch(`${API_URL}/licenses/trial`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ team_id: teamId })
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to create trial license');
            }
            return response.json();
        } catch (error) {
            console.error('Error creating trial license:', error);
            throw error;
        }
    }
}; 